<div class="tr-page">
  <app-project-nav></app-project-nav>

  <app-page-header
    size="compact"
    [title]="'testResults.pageTitle' | translate"
    [subtitle]="'testResults.pageSubtitle' | translate"
  >
    <ng-container right>
      <div class="tr-controls">
        <app-segmented-filter-control
          density="compact"
          [items]="envSegmentedItems()"
          [value]="envFilter()"
          [ariaLabel]="'testResults.envFilterLabel' | translate"
          (valueChange)="setEnv($event)"
        />
        <select
          class="tr-system-select"
          [value]="systemFilter()"
          (change)="setSystem($any($event.target).value)"
          [attr.aria-label]="'testResults.systemFilterLabel' | translate"
        >
          <option value="all">{{ 'testResults.system.all' | translate }}</option>
          @for (opt of systemOptions(); track opt.key) {
            <option [value]="opt.key" [disabled]="opt.deferred">{{ opt.label }}</option>
          }
        </select>
        <a class="tr-secondary-btn" data-testid="ergebnis-ci-zuordnung" [routerLink]="ciMappingLink">
          <mat-icon aria-hidden="true">settings</mat-icon>
          {{ 'testResults.ciMapping' | translate }}
        </a>
        <button type="button" class="tr-secondary-btn tr-import-btn" (click)="openUpload()">
          <span class="tr-secondary-btn__glyph" aria-hidden="true">↓</span>
          {{ 'testResults.upload.button' | translate }}
        </button>
      </div>
    </ng-container>
  </app-page-header>

  <app-release-freigabe
    [projectId]="projectId"
    [releases]="releases()"
    [releaseId]="selectedReleaseId()"
    [releasesState]="releasesState()"
    [environment]="releaseEnvironment()"
    [source]="releaseSource()"
    (releaseChange)="onReleaseChange($event)"
  />

  <div class="tr-view-bar">
    <app-segmented-filter-control
      variant="tabs"
      idPrefix="tr-view-tab"
      controls="tr-view-panel"
      [items]="viewItems()"
      [value]="view()"
      [ariaLabel]="'testResults.views.ariaLabel' | translate"
      (valueChange)="setView($event)"
    />
    <span class="tr-view-hint">{{ 'testResults.views.' + view() + '.hint' | translate }}</span>
  </div>

  <div class="tr-view-panel" id="tr-view-panel" role="tabpanel" [attr.aria-labelledby]="'tr-view-tab-' + view()">
    @if (view() === 'plaene') {
      <app-plan-ergebnis-tabelle
        [projectId]="projectId"
        [releaseId]="selectedReleaseId()"
        [releasesState]="releasesState()"
        [environment]="releaseEnvironment()"
        [source]="releaseSource()"
        (laeufeOeffnen)="openPlanLaeufe($event)"
      />
    } @else if (view() === 'laeufe') {
      <app-lauf-ergebnis-tabelle
        [projectId]="projectId"
        [releaseId]="selectedReleaseId()"
        [releasesState]="releasesState()"
        [environment]="releaseEnvironment()"
        [source]="releaseSource()"
        [planKey]="planFilter()"
        (alleLaeufe)="clearPlanFilter()"
      />
    } @else if (loading()) {
      <div class="tr-state">
        <mat-progress-spinner mode="indeterminate" diameter="40"></mat-progress-spinner>
      </div>
    } @else if (loadError()) {
      <div class="tr-state tr-state--error">{{ 'testResults.loadError' | translate }}</div>
    } @else {
      <div class="tr-kpis">
        <div class="tr-kpi tr-kpi--rate">
          <div class="tr-kpi-top">
            <div>
              <div class="tr-kpi-label">{{ 'testResults.kpi.passRate' | translate }}</div>
              <div class="tr-kpi-value">{{ passRate() }}%</div>
            </div>
            <app-sparkline
              [data]="trend()"
              colorVar="--to-green-dot"
              [width]="120"
              [height]="44"
              [ariaLabel]="'testResults.kpi.passRate' | translate"
            />
          </div>
          <div class="tr-bar">
            <span class="tr-bar-seg tr-bar-seg--passed" [style.width.%]="barWidths().passed"></span>
            <span class="tr-bar-seg tr-bar-seg--failed" [style.width.%]="barWidths().failed"></span>
            <span class="tr-bar-seg tr-bar-seg--open" [style.width.%]="barWidths().openSkipped"></span>
          </div>
        </div>

        <div class="tr-kpi">
          <div class="tr-kpi-icon tr-kpi-icon--neutral"><mat-icon>bar_chart</mat-icon></div>
          <div class="tr-kpi-label">{{ 'testResults.kpi.executed' | translate }}</div>
          <div class="tr-kpi-value tr-kpi-value--neutral">{{ executed() }}</div>
        </div>
        <div class="tr-kpi">
          <div class="tr-kpi-icon tr-kpi-icon--failed"><mat-icon>close</mat-icon></div>
          <div class="tr-kpi-label">{{ 'testResults.kpi.failed' | translate }}</div>
          <div class="tr-kpi-value tr-kpi-value--failed">{{ failed() }}</div>
        </div>
        <div class="tr-kpi">
          <div class="tr-kpi-icon tr-kpi-icon--open"><mat-icon>shield</mat-icon></div>
          <div class="tr-kpi-label">{{ 'testResults.kpi.openSkipped' | translate }}</div>
          <div class="tr-kpi-value tr-kpi-value--open">{{ openSkipped() }}</div>
        </div>
      </div>

      <div class="tr-filters">
        @for (chip of filterChips(); track chip.id) {
          <app-filter-chip
            [label]="chip.labelKey | translate"
            [count]="chip.count"
            [active]="resultFilter() === chip.id"
            [pill]="true"
            (clicked)="setResult(chip.id)"
          />
        }
        <!-- Rollout NEX-788: geteilter Ansichten-Baustein aus NEX-786, rechts neben den Filter-Chips
           (Design-Referenz page-anforderungen-liste.jsx:294-346 / page-testplaene.jsx:239-263). -->
        <app-saved-views-menu
          [views]="savedViewsOptions()"
          [activeName]="activeSavedViewName()"
          [context]="'TEST_RESULTS'"
          [projectId]="projectId"
          (viewSelected)="onSavedViewSelected($event)"
          (viewSaved)="onSavedViewSaved($event)"
          (viewDeleted)="onSavedViewDeleted($event)"
          (viewDeleteFailed)="onSavedViewDeleteFailed()"
        />
        <span class="tr-filters-spacer"></span>
        <span class="tr-history-hint">{{ 'testResults.historyHint' | translate }}</span>
      </div>

      @if (savedViewsError()) {
        <p class="tr-hint tr-hint--error" role="alert">{{ savedViewsError() }}</p>
      }

      <div class="tr-table-card" [class.tr-table-card--busy]="refetching()" [attr.aria-busy]="refetching() || null">
        <div class="tr-table-head">
          <div class="tr-col-key">{{ 'testResults.table.testCase' | translate }}</div>
          <div class="tr-col-title">{{ 'testResults.table.title' | translate }}</div>
          <div class="tr-col-system">{{ 'testResults.table.source' | translate }}</div>
          <div class="tr-col-env">{{ 'testResults.table.environment' | translate }}</div>
          <div class="tr-col-history">{{ 'testResults.table.history' | translate }}</div>
          <div class="tr-col-duration">{{ 'testResults.table.duration' | translate }}</div>
          <div class="tr-col-defects">{{ 'testResults.table.defects' | translate }}</div>
          <div class="tr-col-result">{{ 'testResults.table.lastResult' | translate }}</div>
          <div class="tr-col-chevron"></div>
        </div>

        @if (rows().length === 0) {
          <div class="tr-empty">{{ 'testResults.empty' | translate }}</div>
        }

        @for (row of rows(); track row.testCaseId) {
          <a
            class="tr-row"
            [attr.data-testid]="'test-results-row-' + row.testCaseId"
            [class.tr-row--deferred]="isDeferred(row)"
            [routerLink]="rowLink(row)"
            [attr.aria-label]="rowAriaLabel(row)"
            [attr.aria-describedby]="defectsDescriptionId(row)"
            [attr.aria-disabled]="isDeferred(row) ? 'true' : null"
            [attr.title]="isDeferred(row) ? ('testResults.deferredRowTitle' | translate) : null"
          >
            <div class="tr-col-key">
              @if (row.testCaseKey) {
                <app-code-id-chip [id]="row.testCaseKey" />
              } @else {
                <span class="tr-env-empty">—</span>
              }
            </div>
            <div class="tr-col-title tr-title-cell">{{ row.title }}</div>
            <div class="tr-col-system">
              <app-system-badge
                [kind]="sourceToSystemKey(row.source)"
                [label]="systemMeta(sourceToSystemKey(row.source)).labelKey | translate"
                size="sm"
              />
            </div>
            <div class="tr-col-env">
              @if (row.environment) {
                <app-umgebung-pill [umgebung]="row.environment" [isBranch]="isBranchEnvironment(row.source)" />
              } @else {
                <span class="tr-env-empty">—</span>
              }
            </div>
            <div class="tr-col-history tr-history">
              @for (h of row.history; track $index) {
                <app-matrix-cell
                  [status]="matrixStatus(h)"
                  [tooltip]="'testResults.result.' + statusKey(h) | translate"
                />
              }
            </div>
            <div class="tr-col-duration">{{ formatDuration(row.durationSeconds) }}</div>
            <div class="tr-col-defects tr-defects">
              @for (d of visibleDefects(row); track d.key) {
                <span class="tr-defect-key" [title]="'testResults.defects.linkedBug' | translate">
                  @if (d.blocking) {
                    <span aria-hidden="true">⛔</span>
                  }
                  {{ d.key }}
                </span>
              } @empty {
                <span class="tr-env-empty">—</span>
              }
              @let hidden = hiddenDefects(row);
              @if (hidden.length) {
                <span
                  class="tr-defect-more"
                  aria-hidden="true"
                  [title]="'testResults.defects.more' | translate: { keys: defectKeys(hidden) }"
                >
                  +{{ hidden.length }}
                </span>
              }
              <span class="tr-sr-only" [id]="defectsDescriptionId(row)">{{ defectsDescription(row) }}</span>
            </div>
            <div class="tr-col-result">
              <app-res-pill
                [status]="resPillStatus(row.lastResult)"
                [label]="'testResults.result.' + statusKey(row.lastResult) | translate"
              />
            </div>
            <div class="tr-col-chevron">
              @if (isDeferred(row)) {
                <span class="tr-later">{{ 'testResults.laterBadge' | translate }}</span>
              } @else {
                <mat-icon>chevron_right</mat-icon>
              }
            </div>
            @if (isDeferred(row)) {
              <span class="tr-sr-only">{{ 'testResults.deferredRowTitle' | translate }}</span>
            }
          </a>
        }
      </div>

      <div class="tr-footer">
        <span class="tr-footer__range" role="status">
          {{
            'testResults.footer.rangeLabel'
              | translate: { start: rangeStart(), end: rangeEnd(), total: totalElements() }
          }}
        </span>
        <div class="tr-footer__pager">
          <label class="tr-footer__label" for="tr-page-size">{{ 'testResults.footer.rowsPerPage' | translate }}</label>
          <select
            id="tr-page-size"
            class="tr-footer__select"
            (change)="onPageSizeChange(+pageSizeSelect.value)"
            #pageSizeSelect
            [attr.aria-label]="'testResults.footer.rowsPerPageAriaLabel' | translate"
          >
            @for (size of pageSizeOptions; track size) {
              <option [value]="size" [selected]="pageSize() === size">{{ size }}</option>
            }
          </select>
          @if (pageCount() > 1) {
            <div class="tr-footer__nav">
              <button
                type="button"
                class="tr-action-btn"
                [disabled]="pageIndex() === 0"
                [attr.aria-label]="'testResults.footer.previousPage' | translate"
                (click)="onPageChange(pageIndex() - 1)"
              >
                <mat-icon aria-hidden="true">chevron_left</mat-icon>
              </button>
              <button
                type="button"
                class="tr-action-btn"
                [disabled]="pageIndex() >= pageCount() - 1"
                [attr.aria-label]="'testResults.footer.nextPage' | translate"
                (click)="onPageChange(pageIndex() + 1)"
              >
                <mat-icon aria-hidden="true">chevron_right</mat-icon>
              </button>
            </div>
          }
        </div>
      </div>
    }
  </div>

  <app-robot-upload-modal [projectId]="projectId" [(visible)]="showUploadModal" (imported)="onImported()" />

  <lib-modal
    [title]="'common.unsavedChanges.title' | translate"
    [text]="'common.unsavedChanges.message' | translate"
    [visible]="showDeactivateConfirm()"
    [saveText]="'common.unsavedChanges.leave' | translate"
    [cancelText]="'common.unsavedChanges.stay' | translate"
    (save)="onDeactivateConfirmed()"
    (cancel)="onDeactivateCancelled()"
    (visibleChange)="!$event && onDeactivateCancelled()"
  ></lib-modal>
</div>
